<template>
  <Modal
    classContent="endorsement-modal"
    :active="isOpen"
    :title="modalTitle"
    @close="closeModal"
  >
    <ValidationObserver
      ref="endorsementModalValidationObserver"
      v-slot="{ invalid }"
    >
      <div class="modal-form-body">
        <div class="form-row my-2">
          <div class="form-group col-50">
            <ValidationProvider
              rules="required"
              v-slot="{ errors }"
              name="Tipo de operação"
              immediate
            >
              <label>Tipo de operação</label>
              <vs-input
                v-model="formData.operationType"
                placeholder="Digite o tipo de operação"
                class="form-vs-input"
                :danger="!!errors.length"
                block
              />
              <span v-show="!!errors.length" class="text-danger">
                {{ errors[0] }}
              </span>
            </ValidationProvider>
          </div>
          <div class="form-group">
            <label>Ativo?</label>
            <vs-switch v-model="formData.isActive" />
          </div>
        </div>

        <div class="form-group my-2">
          <ValidationProvider
            rules="required"
            v-slot="{ errors }"
            name="Perfil que pode abrir o endosso"
            immediate
          >
            <label>Perfil que pode abrir o endosso</label>
            <v-select
              v-model="formData.profileCanOpenEndorsement"
              :options="profiles"
              placeholder="Selecione o perfil"
              multiple
              class="form-vs-input"
              :danger="!!errors.length"
            />
            <span v-show="!!errors.length" class="text-danger">
              {{ errors[0] }}
            </span>
          </ValidationProvider>
        </div>

        <div class="form-row my-2">
          <div class="form-group my-2">
            <label>Com movimento financeiro?</label>
            <vs-switch v-model="formData.recalculate" />
          </div>
          <div class="form-group my-2">
            <label>Aceita vigência retroativa?</label>
            <vs-switch v-model="formData.acceptsRetroactiveValidity" />
          </div>

          <div class="form-group my-2 col-33">
            <ValidationProvider
              rules="required|min_value:0|max_value:366"
              v-slot="{ errors }"
              name="Limite de dias futuros para ínicio da vigência"
              immediate
            >
              <label>Limite de dias futuros para início da vigência</label>
              <vs-input
                class="w-full"
                name="additionalDaysAllowedForStartDate"
                id="additionalDaysAllowedForStartDate"
                v-model="formData.additionalDaysAllowedForStartDate"
                type="number"
                :danger="!!errors.length"
              />
              <span v-show="!!errors.length" class="text-danger">
                {{ errors[0] }}
              </span>
            </ValidationProvider>
          </div>
        </div>

        <div v-if="formData.recalculate" class="form-row">
          <div class="form-group col-15">
            <label>Resultado esperado</label>
            <v-select
              v-model="formData.recalculationType"
              :options="recalculationTypes"
              placeholder="Selecione o tipo"
              class="v-select-fixed"
            />
          </div>

          <div class="form-group col-25">
            <label>Método de cálculo</label>
            <v-select
              v-model="formData.timeCalculationMethod"
              :options="timeCalculationMethods"
              placeholder="Selecione o método"
              class="v-select-fixed"
            />
          </div>

          <div class="form-group col-15">
            <label>Custo da operação</label>
            <money
              v-model="formData.cost"
              class="w-full vs-inputx vs-input--input normal"
              name="cost"
              id="cost"
              v-bind="money"
              @input.native="handleMoneyInput($event, { name: 'cost' })"
            ></money>
          </div>

          <div class="form-group col-15">
            <label>Cobrança mínima</label>
            <money
              v-model="formData.minChargeAmountThreshold"
              class="w-full vs-inputx vs-input--input normal"
              name="minChargeAmountThreshold"
              id="minChargeAmountThreshold"
              v-bind="money"
              @input.native="
                handleMoneyInput($event, { name: 'minChargeAmountThreshold' })
              "
            ></money>
          </div>

          <div class="form-group col-15">
            <label>Reembolso mínimo</label>
            <money
              v-model="formData.minRefundAmountThreshold"
              class="w-full vs-inputx vs-input--input normal"
              name="minRefundAmountThreshold"
              id="minRefundAmountThreshold"
              v-bind="money"
              @input.native="
                handleMoneyInput($event, { name: 'minRefundAmountThreshold' })
              "
            ></money>
          </div>
        </div>

        <div class="form-row my-2">
          <div class="form-group">
            <label>É cancelamento?</label>
            <vs-switch v-model="formData.isCancellation" />
          </div>

          <div class="form-group">
            <label>Avaliar exigência de nova vistoria?</label>
            <vs-switch v-model="formData.needInspection" />
          </div>
        </div>

        <div class="form-group my-2">
          <label>Campos editáveis</label>
          <v-select
            v-model="selectedEditableFields"
            :options="editableFieldsOptions"
            label="label"
            class="form-vs-input my-select"
            placeholder="Selecione os campos editáveis"
            multiple
            searchable
            :close-on-select="false"
            :reduce="(item) => item.name"
          >
            <template #selected-option-container="{ option }">
              <vs-chip
                class="mt-2 ml-1"
                transparent
                color="primary"
                closable
                @click="removeEditableField(option.name)"
              >
                {{ option.label }}
              </vs-chip>
            </template>
            <template #option="{ label, name }">
              <div
                :class="{
                  'vs-select--is-selected':
                    selectedEditableFields.includes(name),
                }"
              >
                <feather-icon
                  v-if="selectedEditableFields.includes(name)"
                  icon="CheckCircleIcon"
                  svgClasses="h-4 w-4"
                />
                {{ label }}
              </div>
            </template>
          </v-select>
        </div>
        <div class="form-group my-2">
          <label>Campos de alteração obrigatória</label>
          <v-select
            v-model="selectedMandatoryChangeFields"
            :options="mandatoryChangeFieldsOptions"
            label="label"
            class="form-vs-input my-select"
            placeholder="Selecione os campos de alteração obrigatória"
            multiple
            searchable
            :close-on-select="false"
            :reduce="(item) => item.name"
          >
            <template #selected-option-container="{ option }">
              <vs-chip
                class="mt-2 ml-1"
                transparent
                color="primary"
                closable
                @click="removeMandatoryChangeField(option.name)"
              >
                {{ option.label }}
              </vs-chip>
            </template>
            <template #option="{ label, name }">
              <div
                :class="{
                  'vs-select--is-selected':
                    selectedMandatoryChangeFields.includes(name),
                }"
              >
                <feather-icon
                  v-if="selectedMandatoryChangeFields.includes(name)"
                  icon="CheckCircleIcon"
                  svgClasses="h-4 w-4"
                />
                {{ label }}
              </div>
            </template>
            <template #no-options>
              <p>Selecione pelo menos um campo editável.</p>
            </template>
          </v-select>
        </div>

        <div class="form-row my-2">
          <div class="form-group col-50">
            <label>Motivo I4 Pro</label>
            <v-select
              v-model="formData.i4proReasonCode"
              :options="i4proReasonCodeOptions"
              label="label"
              :reduce="(item) => item.value"
              placeholder="Selecione o motivo"
              class="form-vs-input"
              :loading="i4proReasonCodeOptionsLoading"
              :clearable="true"
            />
          </div>
          <div class="form-group col-50">
            <label>Motivo da Inclusão I4 Pro</label>
            <v-select
              v-model="formData.i4proEndorsementChangeTypeId"
              :options="i4proInclusionReasonOptions"
              label="label"
              :reduce="(item) => item.value"
              placeholder="Selecione o motivo da inclusão"
              class="form-vs-input"
              :loading="i4proInclusionReasonOptionsLoading"
              :clearable="true"
            />
          </div>
        </div>

        <div class="form-row my-2">
          <div class="form-group col-50">
            <label>Tipo de cálculo I4 Pro</label>
            <v-select
              v-model="formData.i4proCalcPremiumTypeId"
              :options="i4proCalculationTypeOptions"
              label="label"
              :reduce="(item) => item.value"
              placeholder="Selecione o tipo de cálculo"
              class="form-vs-input"
              :loading="i4proCalculationTypeOptionsLoading"
              :clearable="true"
            />
          </div>
          <div class="form-group col-50">
            <label>Tipo de cobrança I4 Pro</label>
            <v-select
              v-model="formData.i4proBillingPremiumTypeId"
              :options="i4proChargeTypeOptions"
              label="label"
              :reduce="(item) => item.value"
              placeholder="Selecione o tipo de cobrança"
              class="form-vs-input"
              :loading="i4proChargeTypeOptionsLoading"
              :clearable="true"
            />
          </div>
        </div>

      </div>

      <div class="form-actions">
        <vs-button @click="closeModal" color="dark" outline block>
          Cancelar
        </vs-button>
        <vs-button :disabled="invalid" @click="save" color="success" block>
          Salvar
        </vs-button>
      </div>
    </ValidationObserver>
  </Modal>
</template>

<script>
import { Money } from 'v-money';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import Modal from '../../../../../components/Modal.vue';
import endosso from '../../../../../services/endorsement';
import i4proApi from '../../../../../services/i4pro.api';
import notify from '../../../../../utils/notify';

export default {
  name: 'EndorsementModal',
  components: { Modal, vSelect, Money, ValidationObserver, ValidationProvider },
  props: {
    isOpen: Boolean,
    endorsement: {
      type: Object,
      default: () => null,
    },
    profiles: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      formData: {
        id: null,
        isActive: false,
        operationType: '',
        profileCanOpenEndorsement: '',
        i4proReasonCode: null,
        i4proCalcPremiumTypeId: null,
        i4proBillingPremiumTypeId: null,
        i4proEndorsementChangeTypeId: null,
        recalculate: false,
        recalculationType: '',
        timeCalculationMethod: '',
        editableFields: [],
        mandatoryChangeFields: [],
        isCancellation: false,
        needInspection: false,
        cost: '0',
        minChargeAmountThreshold: '0',
        minRefundAmountThreshold: '0',
        acceptsRetroactiveValidity: false,
        additionalDaysAllowedForStartDate: null,
      },
      selectedEditableFields: [],
      selectedMandatoryChangeFields: [],
      recalculationTypes: ['Prêmio', 'Data de final da vigência'],
      timeCalculationMethods: [
        'Pro rata',
        'Tabela de prazo curto p/ cima',
        'Tabela de prazo curto p/ baixo',
        'Pelo Caixa',
        'Pelo Valor Presente',
      ],
      editableFieldsOptions: [],
      i4proReasonCodeOptions: [],
      i4proReasonCodeOptionsLoading: false,
      i4proCalculationTypeOptions: [],
      i4proCalculationTypeOptionsLoading: false,
      i4proChargeTypeOptions: [],
      i4proChargeTypeOptionsLoading: false,
      i4proInclusionReasonOptions: [],
      i4proInclusionReasonOptionsLoading: false,
      money: {
        decimal: ',',
        thousands: '.',
        prefix: 'R$ ',
        suffix: '',
        precision: 2,
      },
    };
  },
  computed: {
    modalTitle() {
      return this.formData.id
        ? 'Edição de Operação para Endosso'
        : 'Cadastro de Operação para Endosso';
    },
    mandatoryChangeFieldsOptions() {
      return this.editableFieldsOptions.filter((field) =>
        this.selectedEditableFields.includes(field.name),
      );
    },
  },
  watch: {
    endorsement: {
      immediate: true,
      handler(newEndorsement) {
        if (newEndorsement) {
          this.formData = { ...newEndorsement };
          this.selectedEditableFields = newEndorsement.editableFields || [];
          this.selectedMandatoryChangeFields =
            newEndorsement.mandatoryChangeFields || [];
          if (this.profiles.length > 0) {
            this.formData.profileCanOpenEndorsement =
              newEndorsement.profileCanOpenEndorsement
                .map((id) => this.profiles.find((p) => p.id === id))
                .filter(Boolean);
          }
        } else {
          this.resetForm();
        }
      },
    },
    profiles(newProfiles) {
      if (
        this.endorsement &&
        Array.isArray(this.endorsement.profileCanOpenEndorsement)
      ) {
        this.formData.profileCanOpenEndorsement =
          this.endorsement.profileCanOpenEndorsement
            .map((id) => newProfiles.find((p) => p.id === id))
            .filter(Boolean);
      }
    },
    editableFieldsOptions(newOptions) {
      if (this.formData.editableFields && this.formData.editableFields.length) {
        this.selectedEditableFields = this.formData.editableFields;
      }
    },
  },
  methods: {
    handleMoneyInput(event, field) {
      const rawValue = event.target.value.replace(/[^\d,]/g, '');
      const normalized = rawValue.replace(',', '.');
      const parsed = parseFloat(normalized);
      if (field && field.name) {
        this.formData[field.name] = isNaN(parsed) ? 0 : parsed;
      }
    },
    async save() {
      const valid =
        await this.$refs.endorsementModalValidationObserver.validate();
      if (!valid) {
        notify.notifyWarning(
          this.$vs,
          'Preencha todos os campos obrigatórios antes de salvar.',
        );
        return;
      }

      const profileIds = this.formData.profileCanOpenEndorsement.map(
        (profile) => profile.id,
      );

      const payload = {
        isActive: this.formData.isActive ? this.formData.isActive : false,
        operationType: this.formData.operationType,
        profileCanOpenEndorsement: profileIds,
        recalculate: !!this.formData.recalculate,
        recalculationType: this.formData.recalculationType,
        timeCalculationMethod: this.formData.timeCalculationMethod,
        editableFields: this.selectedEditableFields,
        mandatoryChangeFields: this.selectedMandatoryChangeFields,
        isCancellation: this.formData.isCancellation,
        needInspection: this.formData.needInspection,
        i4proReasonCode: this.formData.i4proReasonCode,
        i4proCalcPremiumTypeId: this.formData.i4proCalcPremiumTypeId,
        i4proBillingPremiumTypeId: this.formData.i4proBillingPremiumTypeId,
        i4proEndorsementChangeTypeId:
          this.formData.i4proEndorsementChangeTypeId,
        cost: this.formData.cost,
        minChargeAmountThreshold: this.formData.minChargeAmountThreshold,
        minRefundAmountThreshold: this.formData.minRefundAmountThreshold,
        acceptsRetroactiveValidity: this.formData.acceptsRetroactiveValidity,
        additionalDaysAllowedForStartDate: Number(
          this.formData.additionalDaysAllowedForStartDate,
        ),
      };

      try {
        if (this.formData.id) {
          await endosso.updateOperationType(this.formData.id, payload);
        } else {
          await endosso.createOperationType(payload);
        }

        notify.notifySuccess(
          this.$vs,
          'Tipo de Operação para Endosso, salvo com sucesso!',
          'Sucesso',
        );
      } catch (error) {
        const errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : '';

        notify.notifyFail(
          this.$vs,
          `Erro ao criar o Tipo de Operação. ${errorMessage}`,
        );
      } finally {
        this.$emit('save');
        this.closeModal();
      }
    },
    closeModal() {
      this.$emit('close');
      this.resetForm();
    },
    resetForm() {
      this.formData = {
        id: null,
        isActive: false,
        operationType: '',
        profileCanOpenEndorsement: '',
        i4proReasonCode: null,
        i4proCalcPremiumTypeId: null,
        i4proBillingPremiumTypeId: null,
        i4proEndorsementChangeTypeId: null,
        recalculate: false,
        recalculationType: '',
        timeCalculationMethod: '',
        editableFields: [],
        mandatoryChangeFields: [],
        isCancellation: false,
        needInspection: false,
      };
      this.selectedEditableFields = [];
    },
    async fetchAllEditableFields() {
      try {
        const response = await endosso.getAllEditableFields();
        this.editableFieldsOptions = response.data;
        if (
          this.formData.editableFields &&
          this.formData.editableFields.length
        ) {
          this.selectedEditableFields = this.formData.editableFields;
        }
      } catch (error) {
        notify.notifyFail(this.$vs, 'Erro ao buscar os campos editáveis.');
      }
    },
    async fetchI4ProReasonOptions() {
      if (
        this.i4proReasonCodeOptionsLoading ||
        this.i4proReasonCodeOptions.length
      ) {
        return;
      }
      this.i4proReasonCodeOptionsLoading = true;
      try {
        const options = await i4proApi.getOptions('cd_motivo');
        this.i4proReasonCodeOptions = Array.isArray(options) ? options : [];
      } catch (error) {
        notify.notifyFail(this.$vs, 'Erro ao buscar os motivos do I4 Pro.');
      } finally {
        this.i4proReasonCodeOptionsLoading = false;
      }
    },
    async fetchI4ProCalculationTypeOptions() {
      if (
        this.i4proCalculationTypeOptionsLoading ||
        this.i4proCalculationTypeOptions.length
      ) {
        return;
      }
      this.i4proCalculationTypeOptionsLoading = true;
      try {
        const options = await i4proApi.getOptions('id_tp_calc_premio');
        this.i4proCalculationTypeOptions = Array.isArray(options)
          ? options
          : [];
      } catch (error) {
        notify.notifyFail(
          this.$vs,
          'Erro ao buscar os tipos de cálculo do I4 Pro.',
        );
      } finally {
        this.i4proCalculationTypeOptionsLoading = false;
      }
    },
    async fetchI4ProChargeTypeOptions() {
      if (
        this.i4proChargeTypeOptionsLoading ||
        this.i4proChargeTypeOptions.length
      ) {
        return;
      }
      this.i4proChargeTypeOptionsLoading = true;
      try {
        const options = await i4proApi.getOptions('id_tp_cobranca_premio');
        this.i4proChargeTypeOptions = Array.isArray(options) ? options : [];
      } catch (error) {
        notify.notifyFail(
          this.$vs,
          'Erro ao buscar os tipos de cobrança do I4 Pro.',
        );
      } finally {
        this.i4proChargeTypeOptionsLoading = false;
      }
    },
    async fetchI4ProInclusionReasonOptions() {
      if (
        this.i4proInclusionReasonOptionsLoading ||
        this.i4proInclusionReasonOptions.length
      ) {
        return;
      }
      this.i4proInclusionReasonOptionsLoading = true;
      try {
        const options = await i4proApi.getOptions('id_tp_alteracao_endosso');
        this.i4proInclusionReasonOptions = Array.isArray(options) ? options : [];
      } catch (error) {
        notify.notifyFail(
          this.$vs,
          'Erro ao buscar os motivos de inclusão do I4 Pro.',
        );
      } finally {
        this.i4proInclusionReasonOptionsLoading = false;
      }
    },
    removeEditableField(name) {
      this.selectedEditableFields = this.selectedEditableFields.filter(
        (f) => f !== name,
      );
      this.selectedMandatoryChangeFields =
        this.selectedMandatoryChangeFields.filter((f) => f !== name);
    },
    removeMandatoryChangeField(name) {
      this.selectedMandatoryChangeFields =
        this.selectedMandatoryChangeFields.filter((f) => f !== name);
    },
  },
  created() {
    this.fetchAllEditableFields();
    this.fetchI4ProReasonOptions();
    this.fetchI4ProCalculationTypeOptions();
    this.fetchI4ProChargeTypeOptions();
    this.fetchI4ProInclusionReasonOptions();
  },
};
</script>

<style scoped>
.form-group {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.form-row {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}

.form-actions {
  display: flex;
  justify-content: end;
  gap: 12px;
  margin-top: 24px;
}

.form-vs-input {
  width: 100%;
  max-width: 100%;
}

.vs-select--is-selected {
  color: #00bfed;
  font-weight: bold;
}

.col-15 {
  flex: 0 0 15%;
}

.col-25 {
  flex: 0 0 25%;
}
.col-33 {
  flex: 0 0 33.33%;
}
.col-50 {
  flex: 0 0 calc(50% - 12px);
  max-width: calc(50% - 12px);
}

.v-select-fixed .vs__dropdown-toggle {
  width: 100% !important;
}
.v-select-fixed {
  width: 100%;
}

.modal-form-body {
  max-height: 65vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 6px;
}
</style>
